import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from './useRedux';
import { useLocation, useNavigate } from 'react-router-dom';

import useModal from './useModal';
import { nanoid } from '@reduxjs/toolkit';

import { useSlice } from './useSlice';
import { usePage } from './usePage';

import { HandlerList } from '../types/modal';
import { Options } from '../types/library';

import {
  useDelLibHistoryMutation,
  useDelProjectMutation,
  useDeleteSharedUsersMutation,
  useDeleteShareLinkMutation,
} from '../services/library';
import { useDelSharedProjectMutation } from '../services/share';
import {
  useDelResourceMutation,
  useDeleteDriveSharingLinkMutation,
  useSubmitResourceMutation,
  useUploadLinkMutation,
} from '../services/drive';
import { useDelAllTrashMutation, useDelTrashMutation } from '../services/trash';
import {
  useDelDriveResourceMutation,
  useDelLibResourceMutation,
  useDelTrashResourceMutation,
  useDelAllTrashResourceMutation,
} from '../services/settings';
import { useDeleteStudentMutation } from '../services/student';
import {
  useDeleteGroupMemberMutation,
  useDeleteGroupMutation,
} from '../services/group';

import { selectMenu } from '../features/infoSlice';
import { libShareLinkChanged } from '../features/librarySlice';
import {
  driveShareLinkChanged,
  driveSharedToggled,
} from '../features/driveSlice';
import { selectModalById } from '../features/modalSlice';
import {
  classInfoTypeChanged,
  groupListRemoved,
  groupMemberRemoved,
  groupTotalCntRemove,
  studentAddCntRemoved,
  studentListRemoved,
  studentTotalCntRemoved,
  teacherListRemoved,
  teacherTotalCntRemoved,
} from '../features/classroomSlice';
import { useDeleteTeacherMutation } from '../services/teacher';
import {
  analysisApiSlice,
  useDeleteProjectActivityMutation,
  useDeleteAllActivityMutation,
} from '../services/analysis';
import {
  mySummaryDeleted,
  mySummaryChanged,
  studentProjectActivityListChanged,
  myProjectActivityListChanged,
} from '../features/analysisSlice';

export default function useConfirm(id: string, data: any) {
  const dispatch = useAppDispatch();
  const menu = useAppSelector(selectMenu);
  const modal = useAppSelector((state) => selectModalById(state, id));
  const {
    editModeToggled,
    itemSelected,
    selectSelectedId,
    selectSelectedIds,
    selectOptions,
  } = useSlice();
  const { sortType, sortOrder }: Options = useAppSelector(selectOptions);
  const selectedIds = useAppSelector(selectSelectedIds);
  const projectId = useAppSelector(selectSelectedId);

  const location = useLocation();
  const folderId = location?.state?.folderId;

  const { toggleModal, updateModalData } = useModal();
  const { pageChanged, initPageList } = usePage();
  const navigate = useNavigate();

  const [deleteSharingLink] = useDeleteShareLinkMutation();
  const [deleteSharedUsers] = useDeleteSharedUsersMutation();
  const [deleteHistory] = useDelLibHistoryMutation();

  const [deleteDriveSharingLink] = useDeleteDriveSharingLinkMutation();
  const [uploadLink] = useUploadLinkMutation();
  const [submitResource] = useSubmitResourceMutation();

  const [deleteLibFile] = useDelLibResourceMutation();
  const [deleteDriveFile] = useDelDriveResourceMutation();
  const [deleteTrashFile] = useDelTrashResourceMutation();
  const [deleteAllTrashFile] = useDelAllTrashResourceMutation();
  const [delAllTrash] = useDelAllTrashMutation();

  const [deleteStudent] = useDeleteStudentMutation();
  const [deleteGroup] = useDeleteGroupMutation();
  const [deleteGroupMember] = useDeleteGroupMemberMutation();
  const [deleteTeacher] = useDeleteTeacherMutation();

  const [deleteAllActivity] = useDeleteAllActivityMutation();
  const [deleteProjectActivity] = useDeleteProjectActivityMutation();
  const [getSummary] = analysisApiSlice.useLazyGetSummaryQuery();

  const initPage = () => {
    dispatch(pageChanged(0));
    dispatch(
      initPageList({
        folderId,
        page: 0,
        sortBy: sortType,
        sortDir: sortOrder,
        id: nanoid(),
      })
    );
  };

  const propsByMenu: any = {
    library: {
      mutation: useDelProjectMutation(),
      list: 'projectList',
      id: 'projectId',
    },
    share: {
      mutation: useDelSharedProjectMutation(),
      list: 'projectList',
      id: 'projectId',
    },
    mydrive: {
      mutation: useDelResourceMutation(),
      list: 'resourceList',
      id: 'resourceId',
      callbackReducer: driveSharedToggled(true),
    },
    trash: { mutation: useDelTrashMutation(), list: 'trashList', id: 'seq' },
  };

  const confirmDeleteFile = async () => {
    const prop = propsByMenu[menu];
    const [deleteFile] = prop && prop.mutation;
    const isMydrive = menu === 'mydrive';
    const list = isMydrive
      ? selectedIds
      : selectedIds.map((id) => ({
          [prop.id]: id,
        }));

    try {
      await deleteFile(isMydrive ? list : { [prop.list]: list }).unwrap();
      dispatch(itemSelected({ ids: [], item: null }));
      dispatch(editModeToggled(false));
      prop.callbackReducer && dispatch(prop.callbackReducer);
      initPage();
    } catch (error) {
      console.error('Failed to delete the file', error);
    }
  };

  const confirmStopSharing = async () => {
    toggleModal({
      id: 'projectShare',
      type: 'alert',
      isOpen: true,
    });

    try {
      if (menu === 'library') {
        if (modal?.name === 'shareLink') {
          await deleteSharingLink(projectId).unwrap();
          dispatch(libShareLinkChanged(null));
        } else {
          await deleteSharedUsers(projectId).unwrap();
        }
      } else {
        await deleteDriveSharingLink(projectId).unwrap();
        dispatch(driveShareLinkChanged(null));
      }
      initPage();
    } catch (error) {
      console.error('Failed to delete shared link', error);
    }
  };

  const confirmDeleteHistory = async () => {
    try {
      await deleteHistory({ projectId });
      initPage();
    } catch (error) {
      console.error('Failed to delete history', error);
    }

    toggleModal({
      id: 'projectHistoryList',
      type: 'alert',
      isOpen: true,
    });
  };

  const cancelDeleteHistory = () => {
    toggleModal({
      id: 'projectHistoryList',
      type: 'alert',
      isOpen: true,
    });
  };

  const confirmEmptyTrash = async () => {
    try {
      await delAllTrash().unwrap();
      initPage();
    } catch (error) {
      console.log('Failed to empty trash');
    }
  };

  const cancelStopSharing = () => {
    toggleModal({
      id: 'projectShare',
      type: 'alert',
      isOpen: true,
    });
  };

  const confirmFileReplace = async (data: { overwriteYn: 0 | 1 }) => {
    try {
      if (modal?.data.type === 'web') {
        const uploadLinkRes = await uploadLink({
          data: { name: modal.data.name, url: modal.data.url },
        }).unwrap();
        const { resourceId } = uploadLinkRes.data;
        await submitResource({
          folderId,
          ...data,
          resourceIdList: [resourceId],
        }).unwrap();
      } else {
        modal?.data.handler({ ...data, folderId });
      }

      initPage();
    } catch (error) {
      console.error('Failed to upload drive files', error);
    }
  };

  const confirmBuyStorage = () => {};

  const confirmDeleteStorage = async () => {
    const { location, resourceId } = modal?.data;
    const triggersByLocation: { [key: string]: any } = {
      Lib: deleteLibFile,
      Drive: deleteDriveFile,
      Trash: deleteTrashFile,
    };

    try {
      await triggersByLocation[location](resourceId).unwrap();
    } catch (error) {
      console.error('Failed to delete', error);
    }
  };

  const confirmEmptyTrashStorage = async () => {
    try {
      await deleteAllTrashFile().unwrap();
    } catch (error) {
      console.error('Failed to empty trash', error);
    }
  };

  const confirmRemoveTableRow = async () => {
    const { type, seq, parentSeq } = data;
    try {
      if (type === 'student') {
        await deleteStudent(seq);
        dispatch(studentListRemoved(seq));
        dispatch(studentTotalCntRemoved());
        dispatch(studentAddCntRemoved());
      } else if (type === 'group') {
        await deleteGroup(seq);
        dispatch(groupListRemoved(seq));
        dispatch(groupTotalCntRemove());
      } else if (type === 'groupMember') {
        await deleteGroupMember({
          groupSeq: parentSeq,
          studentSeq: seq,
        });
        dispatch(groupMemberRemoved({ groupSeq: parentSeq, memberSeq: seq }));
      } else {
        await deleteTeacher(seq).unwrap();
        dispatch(teacherListRemoved(seq));
        dispatch(teacherTotalCntRemoved());
      }
    } catch (error) {
      console.error(`Failed to remove table row`, error);
    }
  };

  const confirmDeleteAllActivity = async () => {
    try {
      await deleteAllActivity();
      dispatch(mySummaryDeleted());
      const { data } = await getSummary(null);
      dispatch(mySummaryChanged(data));
    } catch (error) {
      console.error('Failed to delete all activity', error);
    }
  };

  const confirmDeleteActivity = async () => {
    try {
      await deleteProjectActivity(data.projectId);
      dispatch(studentProjectActivityListChanged([]));
      dispatch(myProjectActivityListChanged([]));
    } catch (error) {
      console.error('Failed to delete project activity', error);
    }
  };

  const confirmMoveClass = () => {
    dispatch(classInfoTypeChanged('student'));
    navigate('home/classroom/class', { state: { userId: data.userId } });
  };

  const cancelConvertPdf = () => {
    updateModalData({
      name: 'pdf-converting',
      id: 'pdfConverting',
      type: 'confirm',
      isOpen: false,
      isCancel: true,
    });
  };

  const movePage = (path: string) => {
    navigate(path);
  };

  const confirmHandlers: HandlerList = {
    confirmDeleteFile,
    confirmDeleteShared: confirmDeleteFile,
    confirmStopSharing,
    confirmDeleteHistory,
    confirmEmptyTrash,
    confirmDelCompletly: confirmDeleteFile,
    confirmFileReplace,
    confirmBuyStorage,
    confirmDeleteLibStorage: confirmDeleteStorage,
    confirmDeleteDriveStorage: confirmDeleteStorage,
    confirmDeleteTrashStorage: confirmDeleteStorage,
    confirmEmptyTrashStorage,
    confirmRemoveTableRow,
    confirmDeleteAllActivity,
    confirmDeleteActivity,
    confirmMoveClass,
    'exceeded-storage': () => movePage('home/settings/storage'),
  };

  const cancelHandlers: HandlerList = {
    confirmStopSharing: cancelStopSharing,
    confirmDeleteHistory: cancelDeleteHistory,
    pdfConverting: cancelConvertPdf,
    'exceeded-storage': () => movePage('home/settings/storage'),
  };

  const handleConfirm = useMemo(() => confirmHandlers[id], []);
  const handleCancel = useMemo(() => cancelHandlers[id], []);

  return { handleConfirm, handleCancel };
}
